import React from 'react';
import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/main.css';

const CONTENT = {
  es: {
    title: 'Política de Privacidad',
    lastUpdated: 'Última actualización: Marzo 2026',
    backToHome: 'Volver al inicio',
    sections: [
      {
        title: '1. Información que Recopilamos',
        content: `Recopilamos información que usted nos proporciona directamente, incluyendo:
        
• **Información de contacto**: nombre, email, teléfono, empresa
• **Información del proyecto**: descripción de necesidades, presupuesto, timeline
• **Información técnica**: dirección IP, tipo de navegador, dispositivo
• **Cookies y tecnologías similares**: para mejorar su experiencia

No vendemos ni compartimos su información personal con terceros para fines de marketing.`
      },
      {
        title: '2. Cómo Usamos Su Información',
        content: `Utilizamos la información recopilada para:

• Proporcionar y mejorar nuestros servicios
• Comunicarnos con usted sobre proyectos y actualizaciones
• Enviar información relevante sobre nuestros servicios (con su consentimiento)
• Cumplir con obligaciones legales
• Proteger nuestros derechos y prevenir fraude`
      },
      {
        title: '3. Protección de Datos',
        content: `Implementamos medidas de seguridad técnicas y organizativas para proteger su información:

• Encriptación SSL/TLS para todas las comunicaciones
• Acceso restringido a datos personales
• Monitoreo continuo de seguridad
• Copias de seguridad regulares
• Cumplimiento con estándares de la industria`
      },
      {
        title: '4. Sus Derechos',
        content: `Usted tiene derecho a:

• **Acceso**: Solicitar copia de sus datos personales
• **Rectificación**: Corregir datos inexactos
• **Eliminación**: Solicitar eliminación de sus datos
• **Portabilidad**: Recibir sus datos en formato estructurado
• **Oposición**: Oponerse al procesamiento de sus datos

Para ejercer estos derechos, contáctenos a: privacy@illumidev.com`
      },
      {
        title: '5. Cookies',
        content: `Utilizamos cookies esenciales para el funcionamiento del sitio y cookies analíticas para entender cómo usa nuestro sitio. Puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad.`
      },
      {
        title: '6. Cambios a Esta Política',
        content: `Podemos actualizar esta política periódicamente. Le notificaremos sobre cambios significativos publicando la nueva política en este sitio.`
      },
      {
        title: '7. Contacto',
        content: `Si tiene preguntas sobre esta política de privacidad, contáctenos:

**IllumiDev**
Email: privacy@illumidev.com
Website: illumidev.com`
      }
    ]
  },
  en: {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: March 2026',
    backToHome: 'Back to home',
    sections: [
      {
        title: '1. Information We Collect',
        content: `We collect information you provide directly to us, including:
        
• **Contact information**: name, email, phone, company
• **Project information**: description of needs, budget, timeline
• **Technical information**: IP address, browser type, device
• **Cookies and similar technologies**: to improve your experience

We do not sell or share your personal information with third parties for marketing purposes.`
      },
      {
        title: '2. How We Use Your Information',
        content: `We use the information collected to:

• Provide and improve our services
• Communicate with you about projects and updates
• Send relevant information about our services (with your consent)
• Comply with legal obligations
• Protect our rights and prevent fraud`
      },
      {
        title: '3. Data Protection',
        content: `We implement technical and organizational security measures to protect your information:

• SSL/TLS encryption for all communications
• Restricted access to personal data
• Continuous security monitoring
• Regular backups
• Industry standard compliance`
      },
      {
        title: '4. Your Rights',
        content: `You have the right to:

• **Access**: Request a copy of your personal data
• **Rectification**: Correct inaccurate data
• **Erasure**: Request deletion of your data
• **Portability**: Receive your data in a structured format
• **Objection**: Object to processing of your data

To exercise these rights, contact us at: privacy@illumidev.com`
      },
      {
        title: '5. Cookies',
        content: `We use essential cookies for site functionality and analytics cookies to understand how you use our site. You can configure your browser to reject cookies, although this may affect functionality.`
      },
      {
        title: '6. Changes to This Policy',
        content: `We may update this policy periodically. We will notify you of significant changes by posting the new policy on this site.`
      },
      {
        title: '7. Contact',
        content: `If you have questions about this privacy policy, contact us:

**IllumiDev**
Email: privacy@illumidev.com
Website: illumidev.com`
      }
    ]
  }
};

const PrivacyPage = () => {
  const { language } = useLanguage();
  const t = CONTENT[language];

  return (
    <>
      <Header />
      <main className="legal-page">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={18} /> {t.backToHome}
          </Link>
          
          <div className="legal-header">
            <Shield size={40} className="legal-icon" />
            <h1>{t.title}</h1>
            <p className="legal-updated">{t.lastUpdated}</p>
          </div>
          
          <div className="legal-content">
            {t.sections.map((section, i) => (
              <section key={i} className="legal-section">
                <h2>{section.title}</h2>
                <div className="legal-text">
                  {section.content.split('\n').map((line, j) => (
                    <p key={j} dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(line
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/• /g, '&bull; '))
                    }} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default PrivacyPage;
